import axios from 'axios';
import { usersApiClient } from '@/lib/apiClient';
import {
  ICompleteProfileFormData, IPatchUpdateUserProfileFormData,
  IPutProfileAvatarUpload, IUser, IPatchUserLicenseTypeBody,
  IGetUsersParams,
  IPostPlatformRoleBody,
  IGetUsersResponse,
  IPostAssignLicenseToIndependentUserParams,
  PlatformRoleType,
  IPatchUserPlatformRolesParams,
  IPatchUpdateUserOrganizationParams,
  IGetUserByIdApiField,
} from '@/services/users/types';
import { ILicense } from '@/services/licenses/types';

async function getUserById(
  userUuid: IUser['userUuid'],
  fields?: IGetUserByIdApiField[],
): Promise<IUser> {
  const params = new URLSearchParams();
  if (fields?.length) {
    params.set('fields', fields.join(','));
  }
  const response = await usersApiClient.get(`/users/${userUuid}`, { params });
  return response.data;
}

async function patchCompleteUser(userUuid: IUser['userUuid'], formData: ICompleteProfileFormData) {
  const response = await usersApiClient.patch(`/users/${userUuid}/complete`, formData);
  return response.data;
}

async function getPreSignedProfileAvatarUploadUrl(): Promise<string> {
  const response = await usersApiClient.get('/users/upload-url-avatar');
  return response.data;
}

async function putProfileAvatarUpload({
  presignedUrl,
  binaryFileData,
}: IPutProfileAvatarUpload) {
  const response = await axios.put(presignedUrl, binaryFileData, {
    headers: {
      'Content-Type': binaryFileData.type || 'application/octet-stream',
    },
  });
  return response.data;
}

async function getUsers({
  cursor,
  limit,
  sort = 'createdAt',
  order = 'desc',
  search = '',
  platformRole,
  licenseType,
  fields,
}: IGetUsersParams): Promise<IGetUsersResponse> {
  const params = new URLSearchParams();

  params.set('sort', sort);
  params.set('order', order);

  if (cursor) params.set('page[cursor]', cursor);
  if (limit) params.set('page[limit]', limit.toString());

  if (search) params.set('filter[search]', search);
  if (licenseType) params.set('filter[licenseType]', licenseType);

  if (platformRole) {
    params.set('filter[platformRole]', Array.isArray(platformRole)
      ? platformRole.join(',')
      : platformRole);
  }

  if (fields?.length) {
    params.set('fields', fields.join(','));
  }

  const response = await usersApiClient.get('/users', { params });
  return response.data;
}

async function patchUserLicenseType(userUuid: IUser['userUuid'], formData: IPatchUserLicenseTypeBody) {
  const response = await usersApiClient.patch(`/users/${userUuid}/privileges/license`, formData);
  return response.data;
}

async function postPlatformRole(userUuid: IUser['userUuid'], formData: IPostPlatformRoleBody) {
  const response = await usersApiClient.post(`/users/${userUuid}/privileges/platform`, formData);
  return response.data;
}

async function deletePlatformRole(userUuid: IUser['userUuid'], privilegeName: PlatformRoleType) {
  const response = await usersApiClient.delete(`/users/${userUuid}/privileges/platform/${privilegeName}`);
  return response.data;
}

async function patchUpdateUserProfile(userUuid: IUser['userUuid'], formData: IPatchUpdateUserProfileFormData) {
  const response = await usersApiClient.patch(`/users/${userUuid}`, formData);
  return response.data;
}

async function patchUpdateUserOrganization({
  userUuid,
  orgUuid,
}: IPatchUpdateUserOrganizationParams) {
  const response = await usersApiClient.patch(`/users/${userUuid}/organization`, { orgUuid });
  return response.data;
}

async function getUserLicenseByUserUuid(userUuid: IUser['userUuid']): Promise<ILicense> {
  const response = await usersApiClient.get(`/users/${userUuid}/license`);
  return response.data;
}

async function postAssignLicenseToIndependentUser({
  userUuid,
  product,
  licenseType,
  expiresAt,
}: IPostAssignLicenseToIndependentUserParams) {
  const response = await usersApiClient.post(`/users/${userUuid}/license`, {
    product, licenseType, expiresAt,
  });
  return response.data;
}

async function deleteRevokeLicenseToIndependentUser(userUuid: IUser['userUuid']) {
  const response = await usersApiClient.delete(`/users/${userUuid}/license`);
  return response.data;
}

async function postUserDeletionRequest(userUuid: IUser['userUuid']) {
  const response = await usersApiClient.post(`/users/${userUuid}/deletion-request`);
  return response.data;
}

async function deleteUserDeleteByUserUuid(userUuid: IUser['userUuid']) {
  const response = await usersApiClient.delete(`/users/${userUuid}`);
  return response.data;
}

async function patchUserPlatformRolesByUserUuid({
  userUuid,
  roles,
}: IPatchUserPlatformRolesParams) {
  const response = await usersApiClient.patch(`/users/${userUuid}/roles`, { roles });
  return response.data;
}

export {
  getUserById,
  patchCompleteUser,
  getPreSignedProfileAvatarUploadUrl,
  putProfileAvatarUpload,
  patchUserLicenseType,
  patchUpdateUserProfile,
  postPlatformRole,
  deletePlatformRole,
  getUsers,
  patchUpdateUserOrganization,
  getUserLicenseByUserUuid,
  postAssignLicenseToIndependentUser,
  deleteRevokeLicenseToIndependentUser,
  postUserDeletionRequest,
  deleteUserDeleteByUserUuid,
  patchUserPlatformRolesByUserUuid,
};
